import React, { useEffect, useRef, useState } from 'react';
import CustomTable from '../../share-components/CustomTable';
import { useAppContext } from '../../share-components/AppContext';
import noImage from '../../assets/No_Image_Available.jpg';
import { isEqualNumber, isNullOrEmptyString } from '../../services/UtilityService';
import FloatingCartButton from '../../share-components/FloatingCartButton';
import "./OrderAvailableProduct.scss";
import OrderCart from './OrderCart';
import AlertDialog from '../../share-components/AlertDialog';
import { getActiveCategories, getOrderAvailableProducts } from '../../services/ManagementService';
import useScrollRestoration from '../../share-components/UseScrollRestoration';
import { SEARCH_TEXT_SEPARATOR, SERVER_ERROR_MESSAGE, TBL_DATA_SEPARATOR } from '../../constants/commonConstants';
import NoDataFound from '../../share-components/NoDataFound';

const OrderAvailableProduct = ({ originalOrderCartData, showOrderCart, setParentOrderCartShow, setParentOrderCartHide, setParentOrderCart, setParentLoaded, pageName, isForEdit = false }) => {

    useScrollRestoration(pageName, showOrderCart);

    const { setLoading, setShowApiErrorMessage } = useAppContext();

    const [categoryList, setCategoryList] = useState([]);
    const [orderCountWarningMessage, setOrderCountWarningMessage] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchCategory, setSearchCategory] = useState(0);
    //For showTable
    const [originalProductList, setOriginalProductList] = useState([]);
    const [currentSelectedInventoryItemList, setCurrentSelectedInventoryItemList] = useState([]);

    const [orderCart, setOrderCart] = useState([]);
    const [orderCartTableList, setOrderCartTableList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [isFirstLoaded, setIsFirstLoaded] = useState(false);



    const searchInputRef = useRef(null);
    const cartRef = useRef(null);
    const imgRefs = useRef({});

    const maxDropdownQuantity = 100;

    const fetchActiveCategories = async () => {
        try {
            const allCategory = await getActiveCategories();
            let sortedCategories = (allCategory || [])?.sort((a, b) => (a.categoryName || "")?.localeCompare((b?.categoryName || "")));
            setCategoryList(sortedCategories);
        } catch (error) {
            console.error(error)
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        }
    }

    const fetchAvailableProducts = async (isReload = false) => {
        setLoading(true);
        try {
            const response = await getOrderAvailableProducts();

            //For Edit Order
            const availableProducts = (response?.productData || [])?.map(product => {
                const updatedData = {
                    ...product,
                    inventoryItemData: product?.inventoryItemData?.map((inventoryItem) => {
                        const orderedItem = (originalOrderCartData || [])?.find(originalOrder => originalOrder.productId === product.productId &&
                            originalOrder.inventoryItemId === inventoryItem.inventoryItemId);
                        if (orderedItem) {
                            const actualAvailableQuantity = (parseInt(inventoryItem.availableQuantity) || 0) + (parseInt(orderedItem.orderedQuantity) || 0);
                            const updatedInventoryItem = {
                                ...inventoryItem,
                                availableQuantity: actualAvailableQuantity
                            }
                            return updatedInventoryItem;
                        } else {
                            return inventoryItem;
                        }
                    })
                }
                return updatedData;
            });

            const reArrangeProductData = (availableProducts || []).map(product => {

                const plainText = [
                    product?.brandName,
                    product?.productName,
                    product?.categoryName,
                ].filter(Boolean).join(TBL_DATA_SEPARATOR);

                const data = {
                    ...product,
                    colorList: (product.colorList || []).sort((a, b) => a.color.localeCompare(b.color)),
                    sizeList: (product.sizeList || []).sort((a, b) => a.size.localeCompare(b.size)),
                    text: plainText
                }
                return data;
            })
            if (!isReload) {
                const firstSelectedProductItems = (reArrangeProductData || []).map((product) => getSelectedItemFromProduct(product));
                setCurrentSelectedInventoryItemList(firstSelectedProductItems);
                if (isForEdit) {
                    const convertedOriginalCart = (originalOrderCartData || []).map(orderCart => {
                        const activeTargetProduct = availableProducts?.find(product => isEqualNumber(product.productId, orderCart.productId));
                        return activeTargetProduct ? orderCart : { ...orderCart, isDeactivated: true }
                    });
                    setOrderCart(convertedOriginalCart);
                } else {
                    setOrderCart(originalOrderCartData || []);
                }
            } else {
                const addedProducts = (reArrangeProductData || []).filter(product =>
                    !(currentSelectedInventoryItemList || []).some(selected => isEqualNumber(selected.productId, product.productId)));

                if (addedProducts && addedProducts?.length > 0) {
                    const addedSelectedItems = addedProducts.map(addedProduct => getSelectedItemFromProduct(addedProduct));
                    setCurrentSelectedInventoryItemList(prev => {
                        return [
                            ...prev,
                            ...addedSelectedItems
                        ]
                    });
                }

                setOrderCart(prev => (prev.filter(cart =>
                    isForEdit || availableProducts?.some(product =>
                        isEqualNumber(product.productId, cart.productId)
                    )
                ).map(cart => {
                    const originalProductData = availableProducts?.find(product => isEqualNumber(product.productId, cart.productId));
                    if (!originalProductData) {
                        const oldCartData = originalOrderCartData.find(oldCart => isEqualNumber(oldCart.inventoryItemId, cart.inventoryItemId));
                        return {
                            ...oldCartData,
                            isDeactivated: true
                        }
                    }
                    const originalItemData = originalProductData?.inventoryItemData?.find(item => isEqualNumber(item.inventoryItemId, cart.inventoryItemId));
                    const originalAvailableQuantity = originalItemData?.availableQuantity || 0;
                    const orderedQuantity = cart?.orderedQuantity || 0;
                    const actualAvailableQuantity = originalAvailableQuantity - orderedQuantity;
                    const salePrice = originalItemData?.salePrice || 0;

                    const updatedOrder = {
                        ...cart,
                        availableQuantity: actualAvailableQuantity,
                        salePrice: salePrice,
                        isDeactivated: false
                    };
                    return updatedOrder;
                })));

            }
            setOriginalProductList(reArrangeProductData);

            setIsFirstLoaded(true);
            if (setParentLoaded) {
                setParentLoaded(true);
            }
        } catch (error) {
            console.error(error)
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const getSelectedItemFromProduct = (product) => {
        const selectedColorId = product.colorList?.[0]?.colorId || 0;
        const selectedSizeId = product.sizeList?.[0]?.sizeId || 0;
        const targetItem = product.inventoryItemData.find((item) => isEqualNumber(item.colorId, selectedColorId) &&
            isEqualNumber(item.sizeId, selectedSizeId));
        const selectedInventoryItemId = targetItem?.inventoryItemId || 0;
        const selectedSalePrice = parseInt(targetItem?.salePrice) || 0;

        const firstSelectedProductOfItem = {
            productId: product.productId,
            selectedInventoryItemId: selectedInventoryItemId,
            selectedColorId: selectedColorId,
            selectedSizeId: selectedSizeId,
            selectedQuantity: 1,
            selectedSalePrice: selectedSalePrice,

        }
        return firstSelectedProductOfItem;
    }

    useEffect(() => {
        // setOrderCart(originalOrderCartData);
        fetchActiveCategories();
        fetchAvailableProducts();
    }, []);

    useEffect(() => {
        const showOrderCartData = orderCart?.map((order, index) => {
            return {
                productId: parseInt(order?.productId) || 0,
                showData: getShowOrderCartTableData(order, index)
            }
        });
        setOrderCartTableList(showOrderCartData);
        if (setParentOrderCart) {
            setParentOrderCart(orderCart);
        }
    }, [orderCart, showOrderCart]);

    const getOrderCartCount = () => {
        const totalOrderCount = orderCart?.reduce((sum, item) => sum + item.orderedQuantity, 0);
        return totalOrderCount;
    }

    const getShowOrderAvailableData = (product) => {
        if (!product) return;
        const availableColors = product?.colorList;
        const availableSizes = product?.sizeList;
        const currentSelectedItem = currentSelectedInventoryItemList.find(item => item.productId === product?.productId);
        const selectedColorId = parseInt(currentSelectedItem.selectedColorId) || 0;
        const selectedSizeId = parseInt(currentSelectedItem.selectedSizeId) || 0;
        const selectedInventoryItemId = parseInt(currentSelectedItem.selectedInventoryItemId) || 0;

        const targetInventoryList = product?.inventoryItemData || [];
        const targetItemData = targetInventoryList.find(item => isEqualNumber(item.inventoryItemId, selectedInventoryItemId));
        const salePrice = targetItemData.salePrice;
        const selectedQuantity = currentSelectedItem.selectedQuantity;

        const orderCartItem = orderCart?.find(cart => isEqualNumber(cart.productId, product?.productId) &&
            isEqualNumber(cart.colorId, selectedColorId) && isEqualNumber(cart.sizeId, selectedSizeId));
        const currentOrderCount = orderCartItem?.orderedQuantity || 0;
        const stockCount = (parseInt(targetItemData.availableQuantity) || 0) - currentOrderCount;
        // const plainText = [
        //     product?.brandName,
        //     product?.productName,
        //     product?.categoryName,
        //     stockCount < 0 ? `OutOfStock(${stockCount})` : `InStock (${stockCount})`,
        //     `${salePrice}¥${salePrice}?`,
        // ].filter(Boolean).join(TBL_DATA_SEPARATOR);

        return (<div>
            <div style={{
                fontWeight: '600',
                display: 'flex',
            }}>
                <div style={{ paddingRight: '10px', flex: "1" }}>
                    <div><i className='bi bi-award'></i> {product?.brandName}</div><hr className='m-0' />
                    <div><i className='bi bi-box'></i> {product?.productName}</div><hr className='m-0' />
                    <div><i className='bi bi-tags'></i> {product?.categoryName}</div><hr className='m-0' />
                    {stockCount < 0 ? <><div style={{ color: 'red', }}><i className='bi bi-bag-dash'></i> OutOfStock ({stockCount})</div><hr className='m-0' /></> :
                        <><div><i className='bi bi-bag-check'></i> InStock ({stockCount})</div><hr className='m-0' /></>}
                    <div><i className='bi bi-cash'></i> {salePrice || 0}¥</div>
                </div>
                <div style={{ paddingTop: "5px" }}>
                    <img
                        ref={(el) => (imgRefs.current[product?.productId] = el)}
                        src={product?.productPicture ? `data:image/jpeg;base64,${product?.productPicture}` : noImage}
                        alt={product?.productName}
                        className="img-thumbnail"
                        style={{ width: "var(--orderProductPictureWidth)", height: "var(--orderProductPictureHeight)", objectFit: "cover" }}
                    />
                </div>
            </div>
            <div className='mb-2'>
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'end',
                    flexWrap: 'wrap'
                }}>
                    {availableColors?.length > 0 && (
                        <div className="d-flex flex-column">
                            <label className="custom-label">Color</label>
                            <select className="customInputControl" value={selectedColorId}
                                style={{
                                    minWidth: '80px',
                                    maxWidth: '120px'
                                }}
                                onChange={(e) => handleColorChange(product?.productId, e.target.value)}>
                                {availableColors?.map((colorData, index) => (
                                    <option key={index} value={colorData.colorId}>{colorData.color}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {availableSizes?.length > 0 && (
                        <div className="d-flex flex-column">
                            <label className="custom-label">Size</label>
                            <select className="customInputControl" value={selectedSizeId}
                                style={{
                                    minWidth: '80px',
                                    maxWidth: '120px'
                                }}
                                onChange={(e) => handleSizeChange(product?.productId, e.target.value)}>
                                {availableSizes.map((sizeData, index) => (
                                    <option key={index} value={sizeData.sizeId}>{sizeData.size}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="d-flex flex-column">
                        <label className="custom-label">Quantity</label>
                        <select className="customInputControl" value={selectedQuantity}
                            style={{
                                minWidth: '80px',
                                maxWidth: '120px'
                            }}
                            onChange={(e) => handleQuantityChange(product?.productId, e.target.value)}>
                            {Array.from({ length: maxDropdownQuantity }, (_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-2">
                    <button type='button' className="btn btn-warning btn-sm appBtn"
                        onClick={(e) => {
                            handleAddToOrder(product?.productId);
                            handleAddToCartAnimation(e, product?.productId);
                        }}
                    >Add to Order</button>
                </div>
            </div>
        </div>
        )
        // text: plainText.toLowerCase() // Store searchable text
    };
    // };

    const getShowOrderCartTableData = (cart, index) => {
        const salePrice = (parseInt(cart?.salePrice) || 0);
        const stockCount = (parseInt(cart?.availableQuantity) || 0);
        const isDeactivated = cart?.isDeactivated === true;
        return {
            jsx: (
                <div className={`${isDeactivated ? "disabled-div" : ""}`}>
                    <div style={{
                        fontWeight: '600',
                        display: 'flex',
                    }}>
                        <div style={{ flex: "1", paddingRight: "10px" }}>
                            <div><i className='bi bi-award'></i> {cart?.brandName}</div><hr className='m-0' />
                            <div><i className='bi bi-box'></i> {cart?.productName}</div><hr className='m-0' />
                            <div><i className='bi bi-tags'></i> {cart?.categoryName}</div><hr className='m-0' />
                            {(!isNullOrEmptyString(cart?.color) || !isNullOrEmptyString(cart?.size)) &&
                                <>
                                    <div>
                                        <i className='bi bi-palette'></i> {!isNullOrEmptyString(cart?.color) ? `[${cart?.color}]` : ""}
                                        {!isNullOrEmptyString(cart?.size) ? `[${cart?.size}]` : ""}
                                    </div><hr className='m-0' />
                                </>}
                            <div><i className='bi bi-cash'></i> {salePrice}¥</div><hr className='m-0' />
                            {!isDeactivated && <>
                                {stockCount < 0 ? <div style={{ color: 'red', }}><i className='bi bi-bag-dash'></i> OutOfStock ({stockCount})</div> :
                                    <div><i className='bi bi-bag-check'></i> InStock ({stockCount})</div>}
                            </>}
                        </div>
                        <div style={{ paddingTop: "5px" }}>
                            <img
                                src={cart?.productPicture ? `data:image/jpeg;base64,${cart?.productPicture}` : noImage}
                                alt="picture"
                                className="img-thumbnail"
                                style={{ width: "var(--orderProductPictureWidth)", height: "var(--orderProductPictureHeight)", objectFit: "cover" }}
                            />
                        </div>
                    </div>
                    {!isDeactivated ? <div className='mb-2'>
                        <div style={{
                            display: 'flex',
                            gap: '20px',
                            alignItems: 'end',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "120px",
                                // border: "2px solid yellowgreen"
                                background: "chartreuse",
                                borderRadius: "5px",
                                padding: "0px 5px",
                                fontWeight: 'bold',
                                fontSize: "1rem"

                            }}>
                                {cart?.orderedQuantity > 1 ? <button type='button' className='btn icon-btn'
                                    onClick={() => handleSubstractOrder(cart?.productId, cart?.inventoryItemId)}>
                                    <i className="bi bi-dash-circle"></i>
                                </button> :
                                    <button type='button' className='btn icon-btn'
                                        onClick={() => handleRemoveOrder(cart?.productId, cart?.inventoryItemId)}>
                                        <i className="bi bi-trash3-fill"></i>
                                    </button>
                                }
                                <div>{cart?.orderedQuantity}</div>
                                {/* {orderProductData?.orderedQuantity < orderProductData?.availableQuantity ?  */}
                                <button type='button' className='btn icon-btn'
                                    onClick={() => handlePlusOrder(cart?.productId, cart?.inventoryItemId)}>
                                    <i className="bi bi-plus-circle"></i>
                                </button>
                            </div>
                            <button type='button' className='btn btn-sm btn-danger'
                                onClick={() => handleRemoveOrder(cart?.productId, cart?.inventoryItemId)}><i className="bi bi-trash3-fill"></i>
                            </button>
                        </div>
                    </div> :
                        <>
                            <div style={{
                                width: "120px",
                                // border: "2px solid yellowgreen"
                                background: "chartreuse",
                                borderRadius: "5px",
                                padding: "0px 5px",
                                fontWeight: 'bold',
                                fontSize: "1rem",
                                textAlign: "center",
                                padding: "3px 0px",
                            }}>{cart?.orderedQuantity}</div>
                            <div className="fw-bold text-danger">
                                This Product is Deactivated!
                            </div>
                        </>
                    }
                </div>
            )
        };
    };

    const handleSubstractOrder = (productId, inventoryItemId) => {
        productId = parseInt(productId) || 0;
        inventoryItemId = parseInt(inventoryItemId) || 0;

        setOrderCart(prev => {
            // Ensure prev is an array
            if (!Array.isArray(prev)) {
                return [];
            }

            // Find the target order
            const targetOrder = prev.find(order => isEqualNumber(order.productId, productId) && isEqualNumber(order.inventoryItemId, inventoryItemId));

            if (!targetOrder) {
                return prev;
            }

            const updatedQuantity = (parseInt(targetOrder.orderedQuantity) || 0) - 1;

            if (updatedQuantity <= 0) {
                // Remove the item if orderedQuantity becomes 0
                return prev.filter((order) => !(isEqualNumber(order.productId, productId) && isEqualNumber(order.inventoryItemId, inventoryItemId)));
            } else {
                // Decrease orderedQuantity
                return prev.map((order) =>
                    isEqualNumber(order.productId, productId) && isEqualNumber(order.inventoryItemId, inventoryItemId) ?
                        { ...order, orderedQuantity: updatedQuantity, availableQuantity: ((parseInt(targetOrder.availableQuantity) || 0) + 1) } : order
                );
            }
        });
    }

    const handlePlusOrder = (productId, inventoryItemId) => {
        productId = parseInt(productId) || 0;
        inventoryItemId = parseInt(inventoryItemId) || 0;

        setOrderCart(prev => {
            // Ensure prev is an array
            if (!Array.isArray(prev)) {
                return [];
            }

            // Find the target order
            const targetOrder = prev.find(order => isEqualNumber(order.productId, productId) && isEqualNumber(order.inventoryItemId, inventoryItemId));

            if (!targetOrder) {
                return prev;
            }

            const updatedQuantity = (parseInt(targetOrder.orderedQuantity) || 0) + 1;

            // Decrease orderedQuantity
            return prev.map((order, index) =>
                isEqualNumber(order.productId, productId) && isEqualNumber(order.inventoryItemId, inventoryItemId) ?
                    { ...order, orderedQuantity: updatedQuantity, availableQuantity: ((parseInt(targetOrder.availableQuantity) || 0) - 1) } : order
            );
        });
    }

    const handleRemoveOrder = (productId, inventoryItemId) => {
        productId = parseInt(productId) || 0;
        inventoryItemId = parseInt(inventoryItemId) || 0;

        setOrderCart(prev => {
            const result = prev.filter((order) => !(isEqualNumber(order.productId, productId) && isEqualNumber(order.inventoryItemId, inventoryItemId)));
            return result;
        });
    }

    const handleSizeChange = (productId, sizeId) => {
        const selectedSizeId = parseInt(sizeId) || 0;
        if (selectedSizeId <= 0) {
            return;
        }
        setCurrentSelectedInventoryItemList(prev => {
            const targetInventoryItemList = originalProductList?.find(data => data.productId === productId)?.inventoryItemData || [];
            const existingSelectedProduct = prev?.find(data => data.productId === productId);
            const targetInventoryItem = targetInventoryItemList?.find(data => isEqualNumber(data.sizeId, selectedSizeId) &&
                isEqualNumber(data.colorId, existingSelectedProduct?.selectedColorId));

            const availableQuantity = parseInt(targetInventoryItem?.availableQuantity) || 0;
            const salePrice = parseInt(targetInventoryItem?.salePrice) || 0;
            const inventoryItemId = targetInventoryItem?.inventoryItemId;
            const updatedData = {
                ...existingSelectedProduct,
                selectedInventoryItemId: inventoryItemId,
                selectedSizeId: targetInventoryItem?.sizeId,
                selectedSalePrice: salePrice
            }
            return prev.map(item => item.productId === productId ? updatedData : item);
        });
    }

    const handleColorChange = (productId, colorId) => {
        const selectedColorId = parseInt(colorId) || 0;
        if (selectedColorId <= 0) {
            return;
        }

        setCurrentSelectedInventoryItemList(prev => {
            const taegetProduct = originalProductList?.find(data => data.productId === productId);
            const targetInventoryItemList = taegetProduct?.inventoryItemData || [];
            const existingSelectedProduct = prev?.find(data => data.productId === productId);
            const targetInventoryItem = targetInventoryItemList?.find(data => isEqualNumber(data.sizeId, existingSelectedProduct?.selectedSizeId) &&
                isEqualNumber(data.colorId, selectedColorId));
            // const availableQuantity = parseInt(targetInventoryItem?.availableQuantity) || 0;
            const salePrice = parseInt(targetInventoryItem?.salePrice) || 0;
            const inventoryItemId = targetInventoryItem?.inventoryItemId;

            const updatedData = {
                ...existingSelectedProduct,
                selectedInventoryItemId: inventoryItemId,
                selectedColorId: targetInventoryItem?.colorId,
                // selectedColor: targetInventoryItem?.color,
                selectedSalePrice: salePrice
            }
            return prev.map(item => item.productId === productId ? updatedData : item);
        });
    }

    const handleQuantityChange = (productId, quantity) => {
        const selectedQuantity = parseInt(quantity) || 0;
        if (selectedQuantity <= 0) {
            return;
        }
        setCurrentSelectedInventoryItemList(prev => {
            const existingData = prev?.find(data => data.productId === productId);
            const updatedData = {
                ...existingData,
                selectedQuantity: selectedQuantity
            }
            return prev.map(item => item.productId === productId ? updatedData : item);
        });
    }

    const columns = [
        {
            Header: 'Order Available Infos',
            accessor: 'showData',
            className: 'no-selectable-text',
            disableSortBy: true,
            style: {
                minWidth: "320px",
                // maxWidth: "400px"
            },
            Cell: ({ row }) => getShowOrderAvailableData(row.original),
        }
    ];

    const handleAddToOrder = (orderProductId) => {
        const productId = parseInt(orderProductId) || 0;
        const targetProduct = originalProductList?.find(data => data.productId === productId);
        const currentSelectedItem = currentSelectedInventoryItemList?.find(data => data.productId === productId);
        if (!targetProduct || !currentSelectedItem) {
            return;
        }


        const selectedInventoryItemId = parseInt(currentSelectedItem?.selectedInventoryItemId) || 0;
        const selectedQuantity = parseInt(currentSelectedItem?.selectedQuantity) || 0;
        const selectedColorId = parseInt(currentSelectedItem?.selectedColorId) || 0;
        const selectedColor = targetProduct.colorList?.find(colorInfo => isEqualNumber(colorInfo.colorId, selectedColorId))?.color || "";
        const selectedSizeId = parseInt(currentSelectedItem?.selectedSizeId) || 0;
        const selectedSize = targetProduct.sizeList?.find(sizeInfo => isEqualNumber(sizeInfo.sizeId, selectedSizeId))?.size || "";
        const selectedSalePrice = parseInt(currentSelectedItem?.selectedSalePrice) || 0;

        const originalItemData = targetProduct.inventoryItemData.find(item => isEqualNumber(item.inventoryItemId, selectedInventoryItemId));
        const existingOrder = orderCart?.find(data => isEqualNumber(data.productId, productId) && isEqualNumber(data.inventoryItemId, selectedInventoryItemId));
        const originalAvailableQuantity = originalItemData?.availableQuantity || 0;
        const previousOrderedCartQuantity = existingOrder?.orderedQuantity || 0;
        const totalOrderedQuantity = previousOrderedCartQuantity + selectedQuantity;
        const actualAvailableQuantity = originalAvailableQuantity - totalOrderedQuantity;

        if (existingOrder) {
            const updatedOrder = {
                ...existingOrder,
                orderedQuantity: totalOrderedQuantity,
                availableQuantity: actualAvailableQuantity
            }
            setOrderCart(prev => (
                prev?.map(order => order.productId === productId && order.inventoryItemId === selectedInventoryItemId ? updatedOrder : order)
            ));
        } else {
            setOrderCart(prev => {
                const newOrder = {
                    productId: productId,
                    categoryName: targetProduct?.categoryName,
                    brandName: targetProduct?.brandName,
                    productName: targetProduct?.productName,
                    productPicture: targetProduct?.productPicture,
                    inventoryItemId: selectedInventoryItemId,
                    colorId: selectedColorId,
                    color: selectedColor,
                    sizeId: selectedSizeId,
                    size: selectedSize,
                    availableQuantity: actualAvailableQuantity,
                    orderedQuantity: totalOrderedQuantity || 0,
                    salePrice: selectedSalePrice,
                };
                const result = [
                    ...prev,
                    newOrder
                ];
                return result;
            });
        }
    }

    const handleAddToCartAnimation = (e, productId) => {
        const cart = cartRef.current;
        const img = imgRefs.current[productId];
        if (!img || !cart) return;

        const rectImg = img.getBoundingClientRect();
        const rectCart = cart.getBoundingClientRect();

        const clone = img.cloneNode(true);
        // clone.classList.remove('img-thumbnail');
        clone.classList.add('flying-img-clone');
        document.body.appendChild(clone);

        // Initial position
        clone.style.left = `${rectImg.left}px`;
        clone.style.top = `${rectImg.top}px`;
        clone.style.width = `${rectImg.width}px`;
        clone.style.height = `${rectImg.height}px`;

        // Calculate travel distance
        const deltaX = rectCart.left + rectCart.width / 2 - (rectImg.left + rectImg.width / 2);
        const deltaY = rectCart.top + rectCart.height / 2 - (rectImg.top + rectImg.height / 2);

        const finalScale = 0.5; // ? Adjust to control size at cart

        // Animate to cart
        requestAnimationFrame(() => {
            clone.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${finalScale})`;
            clone.style.opacity = '1';
        });

        // After fly: reset to final position + bounce
        setTimeout(() => {
            // Get final computed position after transform
            const finalLeft = rectImg.left + deltaX;
            const finalTop = rectImg.top + deltaY;

            // Freeze in place at cart center
            clone.style.transition = 'none';
            clone.style.transform = 'none'; // clear translate/scale
            clone.style.left = `${finalLeft}px`;
            clone.style.top = `${finalTop}px`;
            clone.style.width = `${rectImg.width * 0.5}px`;
            clone.style.height = `${rectImg.height * 0.5}px`;

            // Trigger bounce
            clone.classList.add('bounce-only');
        }, 300);

        // Remove after bounce
        setTimeout(() => {
            clone.remove();
        }, 700);
    };

    useEffect(() => {
        if (!originalProductList) return;

        let filteredList = originalProductList || [];

        if ((parseInt(searchCategory) || 0) <= 0) {
            filteredList = originalProductList || [];
        } else {
            const filteredData = originalProductList?.filter(product => product?.categoryId === searchCategory) || [];
            filteredList = filteredData;
        }

        if (!searchText) {
            setFilteredData(filteredList);
        } else {
            const searchWords = searchText.trim().toLowerCase().split(SEARCH_TEXT_SEPARATOR);
            const filter = filteredList.filter(item => {
                const targetTextArr = (item.text.toLowerCase() || '').split(TBL_DATA_SEPARATOR);
                return searchWords.every(word => targetTextArr.some(str => str?.trim().includes(word?.trim())));
            });
            setFilteredData(filter);
        }
    }, [searchText, searchCategory, originalProductList]);

    return (
        <div id='orderAvailableProductPage'>
            {isFirstLoaded && <>
                <div
                    style={{
                        display: `${showOrderCart === true ? 'block' : 'none'}`
                    }}
                >
                    <OrderCart
                        orderCount={getOrderCartCount() || 0}
                        tableData={orderCartTableList || []}
                        onClickAddNoOrderButton={setParentOrderCartHide}
                    />
                </div>

                <div
                    style={{
                        display: `${showOrderCart === false ? 'block' : 'none'}`
                    }}
                >
                    <button type='button' className='reload-btn' title='reload' onClick={() => fetchAvailableProducts(true)}><i className='bi bi-arrow-clockwise'></i></button>

                    <div style={{ maxWidth: '550px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                            <div className="d-inline">
                                <label className="me-1 custom-label">
                                    Category Name
                                </label>
                                <div>
                                    <select className='customInputControl me-2 select-min-max-width' value={searchCategory}
                                        style={{
                                            minWidth: '115px',
                                            maxWidth: '150px'
                                        }}
                                        onChange={(e) => setSearchCategory((parseInt(e.target.value) || 0))}>
                                        <option value='0'>All</option>
                                        {categoryList?.map((category) => (
                                            <option key={category.categoryId} value={category?.categoryId}>{category?.categoryName}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <input ref={searchInputRef} className='customInputControl txt-search' type='text' placeholder='Search Text' value={searchText} autoComplete="off"
                                    onChange={(e) => setSearchText(e.target.value)} style={{ paddingRight: '30px' }} />
                                <button type='button' className='icon-btn input-icon'
                                    style={{
                                        pointerEvents: `${!searchText ? 'none' : 'auto'}`,
                                        color: `${!searchText ? '' : 'red'}`
                                    }}
                                    onClick={() => {
                                        if (searchText) {
                                            setSearchText("");
                                            setTimeout(() => {
                                                searchInputRef.current?.focus();
                                            }, 0);
                                        }
                                    }}
                                >
                                    <i className={`bi ${!searchText ? 'bi-search' : 'bi-x-circle'}`}></i>
                                </button>
                            </div>
                        </div>
                        {filteredData?.length > 0 ?
                            <CustomTable
                                tableId='orderAvailableProductListTbl'
                                columns={columns}
                                tblData={filteredData || []}
                                showPagination={true}
                                itemsPerPage={10}
                                // initialSortBy={[{ id: 'showData', desc: false }]}
                                noHeader={true}
                                isDoublePagination={true}
                            // noBordered={true}
                            /> : <NoDataFound />}
                    </div>
                    <FloatingCartButton cartRef={cartRef}
                        cartCount={getOrderCartCount() || 0}
                        clickCallBack={setParentOrderCartShow}
                    />
                </div>
                <AlertDialog
                    dialogId={'orderCountWarningDialog'}
                    showDialog={!isNullOrEmptyString(orderCountWarningMessage)}
                    body={orderCountWarningMessage}
                    onConfirm={() => setOrderCountWarningMessage(null)}
                />
            </>}
        </div>
    );
};

export default OrderAvailableProduct;